import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layouts/DashboardLayout';
import TransferTabs from '../components/TransferTabs';
import QuickActions from '../components/QuickActions';
import TransactionReceiptModal from '../components/TransactionReceiptModal';
import SummaryCards from '../components/dashboard/SummaryCards';
import AccountCards from '../components/dashboard/AccountCards';
import CashFlowChart from '../components/dashboard/CashFlowChart';
import UpcomingPayments from '../components/dashboard/UpcomingPayments';
import PromoCard from '../components/dashboard/PromoCard';
import AlertBanner from '../components/dashboard/AlertBanner';
import { 
  getUserData, 
  subscribeToAccountUpdates, 
  subscribeToTransactions,
  getRecentTransactions,
  processTransfer,
  updateAccountNumbers
} from '../utils/firestore';
import { getMockTransactions } from '../utils/mockData';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTransactions, setAllTransactions] = useState([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [txFilter, setTxFilter] = useState('all'); // 'all' | 'in' | 'out'
  const showMockRef = useRef(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = String(number).replace(/\D/g, '');
    if (cleaned.length === 8) {
      return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`;
    }
    return number;
  };

  const handleCopyClick = async (accountNumber, accountType) => {
    try {
      await navigator.clipboard.writeText(String(accountNumber).replace(/\s/g, ''));
      setCopiedAccount(accountType);
      setTimeout(() => setCopiedAccount(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getCurrentBalance = (accounts) => {
    const currentAccount = accounts.find(acc => acc.type === 'Current' || acc.type === 'Checking');
    return currentAccount ? (currentAccount.available ?? currentAccount.balance ?? 0) : 0;
  };

  const getTotalAvailable = (accounts) => {
    return accounts.reduce((sum, acc) => sum + (acc.available ?? acc.balance ?? 0), 0);
  };

  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadInitialData = async () => {
      try {
        const data = await getUserData(currentUser.uid);
        const updatedAccounts = await updateAccountNumbers(currentUser.uid);
        if (updatedAccounts) {
          setAccounts(updatedAccounts);
        } else {
          setAccounts(data.accounts || []);
        }
        setUserData(data);
        showMockRef.current = data.showMockTransactions !== false;
        setIsNewUser(!!data.registrationDate);

        try {
          const initial = await getRecentTransactions(currentUser.uid);
          setAllTransactions(initial);
        } catch (txErr) {
          if (data.showMockTransactions !== false) {
            setAllTransactions([...getMockTransactions()].sort((a, b) => new Date(b.date) - new Date(a.date)));
          } else {
            setAllTransactions([]);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    const unsubscribeAccounts = subscribeToAccountUpdates(
      currentUser.uid,
      (updatedAccounts) => setAccounts(updatedAccounts)
    );

    const unsubscribeTransactions = subscribeToTransactions(
      currentUser.uid,
      (realTransactions) => {
        const list = showMockRef.current
          ? [...realTransactions, ...getMockTransactions()]
          : realTransactions;
        const normalized = list
          .map((t) => ({ ...t, date: t.date instanceof Date ? t.date : new Date(t.date) }))
          .sort((a, b) => b.date.getTime() - a.date.getTime());
        setAllTransactions(normalized);
      }
    );

    return () => {
      unsubscribeAccounts();
      unsubscribeTransactions();
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    showMockRef.current = userData?.showMockTransactions !== false;
  }, [userData?.showMockTransactions]);

  const handleTransfer = async (transferData) => {
    try {
      await processTransfer({
        ...transferData,
        userId: currentUser.uid
      });
    } catch (error) {
      console.error('Transfer error:', error);
      setErrorMessage('An error occurred while processing your transfer. Please try again or contact support if the issue persists.');
      throw error;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTransactionIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'transfer':
        return '↔️';
      case 'bill pay':
        return '💰';
      case 'deposit':
        return '📱';
      case 'income':
        return '↓';
      default:
        return '·';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  const currentBalance = getCurrentBalance(accounts);
  const totalAvailable = getTotalAvailable(accounts);
  const now = new Date();
  const monthLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const monthCredits = allTransactions.filter(t => {
    const d = new Date(t.date);
    return t.type === 'credit' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthDebits = allTransactions.filter(t => {
    const d = new Date(t.date);
    return t.type === 'debit' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthIn = monthCredits.reduce((s, t) => s + Math.abs(t.amount), 0);
  const monthOut = monthDebits.reduce((s, t) => s + Math.abs(t.amount), 0);
  const pendingTotal = allTransactions.filter(t => t.status === 'pending').reduce((s, t) => s + Math.abs(t.amount), 0);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const filteredTransactions = allTransactions.filter(t => {
    if (txFilter === 'in') return t.type === 'credit';
    if (txFilter === 'out') return t.type === 'debit';
    return true;
  });

  return (
    <DashboardLayout>
      {errorMessage && (
        <div className="sticky top-14 sm:top-16 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-2 pb-2 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 shadow-sm">
            <p className="text-sm text-red-800 dark:text-red-200 flex-1">{errorMessage}</p>
            <button
              type="button"
              onClick={() => setErrorMessage('')}
              className="shrink-0 p-1 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="mb-6">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-0.5">
          {greeting()}, {userData?.name?.split(' ')[0] || 'User'}
        </h1>
      </header>

      {/* Summary cards */}
      <section className="mb-6">
        <SummaryCards
          formatCurrency={formatCurrency}
          availableBalance={totalAvailable}
          monthIn={monthIn}
          monthOut={monthOut}
          pendingTotal={pendingTotal}
        />
      </section>

      {/* New user alert */}
      {isNewUser && (
        <section className="mb-6">
          <AlertBanner isNewUser={isNewUser} onDismiss={() => setAlertDismissed(true)} dismissed={alertDismissed} />
        </section>
      )}

      {/* Accounts + Transfer | Chart + Upcoming — two columns on lg */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 space-y-6">
          <section id="accounts-section">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">My accounts</h2>
            <AccountCards
              accounts={accounts}
              formatCurrency={formatCurrency}
              handleCopyClick={handleCopyClick}
              copiedAccount={copiedAccount}
              userData={userData}
            />
          </section>

          <section id="transfer-section">
            <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
              <div className="border-b border-slate-100 dark:border-slate-700 px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900 dark:text-white">Pay & transfer</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Between accounts, to another bank, or to a Willowvale member</p>
                </div>
                <a
                  href="#transfer-section"
                  className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  New transfer
                </a>
              </div>
              <TransferTabs
                accounts={accounts}
                onTransfer={handleTransfer}
                currentUser={currentUser}
              />
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">Cash flow</h2>
            <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 shadow-sm">
              <CashFlowChart moneyIn={monthIn} moneyOut={monthOut} monthLabel={monthLabel} />
            </div>
          </section>
          <section>
            <UpcomingPayments formatCurrency={formatCurrency} />
          </section>
          <section className="hidden lg:block">
            <PromoCard />
          </section>
        </div>
      </div>

      {/* Quick actions */}
      <section id="quick-actions" className="mb-8">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">Quick actions</h2>
        <QuickActions accounts={accounts} />
      </section>

      {/* Recent transactions */}
      <section id="transactions-section">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Recent activity</h2>
          <div className="flex items-center gap-2">
            {['all', 'in', 'out'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTxFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  txFilter === key
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {key === 'all' ? 'All' : key === 'in' ? 'In' : 'Out'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => document.getElementById('quick-actions')?.scrollIntoView({ behavior: 'smooth' })}
              className="ml-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              Statements
            </button>
          </div>
        </div>

        {filteredTransactions.length > 0 ? (
          <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
            <ul className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredTransactions.slice(0, 15).map((transaction, index) => (
                <li key={transaction.id || index}>
                  <button
                    type="button"
                    onClick={() => setReceiptTransaction(transaction)}
                    className="w-full text-left px-4 py-3.5 hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-inset"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 text-slate-600 dark:text-slate-300 font-medium">
                        {getTransactionIcon(transaction.category)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{transaction.description}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(transaction.date)}</p>
                      </div>
                    </div>
                    <span className={`shrink-0 font-semibold tabular-nums ${transaction.type === 'credit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-slate-100'}`}>
                      {transaction.type === 'credit' ? '+' : '−'}{formatCurrency(Math.abs(transaction.amount))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm px-4 py-12 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">No transactions to show</p>
          </div>
        )}
      </section>

      {receiptTransaction && (
        <TransactionReceiptModal
          transaction={receiptTransaction}
          userData={userData}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onClose={() => setReceiptTransaction(null)}
        />
      )}
    </DashboardLayout>
  );
}
